// eslint-disable-next-line no-restricted-imports
import { Box, Loader, Text } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { PAYMENT_TYPE_TO_METHOD, PaymentType, TransactionType } from '~/common/enums/payments.enum';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { Button } from '~/domains/design-system/Button';
import dt from '~/testing';

import { useAvailablePaymentMethods } from '../hooks/useAvailablePaymentMethods';
import { useGetMinimumTransactionAmount } from '../hooks/useGetMinimumTransactionAmount';
import { useProcessingFees } from '../hooks/useProcessingFees';
import { useWalletBalance } from '../hooks/useWalletBalance';

import { useWithdrawalFormContext } from './context/withdrawal.context';

type WithdrawalActionsProps = {
  isWithdrawing: boolean;
};

function WithdrawalActions({ isWithdrawing }: WithdrawalActionsProps) {
  const { t } = useTranslation('payments');

  const { data: balance } = useWalletBalance();
  const form = useWithdrawalFormContext();
  const { values } = form;
  const { paymentType, withdrawalAmount, cardOption } = values;

  const { calculateFee } = useProcessingFees({
    paymentMethod: PAYMENT_TYPE_TO_METHOD[paymentType],
    transactionType: TransactionType.WITHDRAWAL,
  });

  const getMinimumTransactionAmount = useGetMinimumTransactionAmount({
    transactionType: TransactionType.WITHDRAWAL,
  });
  const minWithdrawalAmount = getMinimumTransactionAmount(
    PAYMENT_TYPE_TO_METHOD[paymentType as PaymentType]
  );

  const { availablePaymentMethods, isLoadingAvailablePaymentMethods } = useAvailablePaymentMethods({
    transactionType: TransactionType.WITHDRAWAL,
  });

  const isCurrentPaymentTypeDisabled = availablePaymentMethods?.some(
    (item) =>
      item.paymentMethod === PAYMENT_TYPE_TO_METHOD[paymentType] && item.status === 'disabled'
  );

  const formattedAmount =
    withdrawalAmount >= minWithdrawalAmount
      ? CurrencyConvertor(withdrawalAmount - calculateFee(withdrawalAmount))
      : '';
  const isDisabled =
    isLoadingAvailablePaymentMethods ||
    isCurrentPaymentTypeDisabled ||
    balance?.withdrawableBalance === 0 ||
    balance?.withdrawableBalance < form.values.withdrawalAmount ||
    form.values.withdrawalAmount < minWithdrawalAmount ||
    (paymentType === PaymentType.CARD && (typeof withdrawalAmount !== 'number' || !cardOption));

  return (
    <Tooltip
      data-test-id={dt.payments.withdrawal.withdrawButtonTooltip}
      withArrow
      isRendered={isDisabled}
      labels={[
        {
          label: t('withdrawal.form.actions.tooltip.disabledPaymentType'),
          condition: isCurrentPaymentTypeDisabled,
        },
        {
          label: t('withdrawal.form.actions.tooltip.insufficientBalance'),
          condition:
            balance?.withdrawableBalance === 0 || balance?.withdrawableBalance < withdrawalAmount,
        },
        {
          label: t('withdrawal.form.actions.tooltip.minimum', {
            amount: CurrencyConvertor(minWithdrawalAmount),
          }),
          condition: withdrawalAmount < minWithdrawalAmount || typeof withdrawalAmount !== 'number',
        },
        {
          label: t('withdrawal.form.actions.tooltip.emptyCard'),
          condition: !cardOption,
        },
      ]}
    >
      <Box>
        <Button
          data-test-id={dt.payments.withdrawal.withdrawButton}
          variant="primary-fill"
          disabled={isDisabled || isWithdrawing}
          type="submit"
          size="large"
          fullWidth
        >
          {isWithdrawing ? (
            <Loader color="white" />
          ) : (
            <Text>{t('withdrawal.form.actions.withdraw', { amount: formattedAmount })}</Text>
          )}
        </Button>
      </Box>
    </Tooltip>
  );
}

export default WithdrawalActions;
